import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Building2, Heart, Radius } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import type { DirectoryExpert, IssueRef } from "../../types";

const MAX_ISSUES = 4;

/** A single thematic-coloured expertise chip. */
function IssueChip({ issue }: { issue: IssueRef }) {
  const theme = thematicClasses(issue.css_class);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold",
        theme.softBg,
        theme.ink,
      )}
    >
      {issue.name}
    </span>
  );
}

/** Initials for the monogram: primary contact, falling back to the org name. */
function initials(expert: DirectoryExpert): string {
  const first = expert.contact_primary_first_name?.trim();
  const last = expert.contact_primary_last_name?.trim();
  if (first || last) {
    return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
  }
  return expert.organization_name.slice(0, 2).toUpperCase();
}

/**
 * Directory result card: the public summary of a validated expert. Leads with a
 * monogram + organisation, surfaces where the expert is based and served scope,
 * and colour-codes covered issues with the four-theme palette. The favorite
 * toggle and profile link are the two actions (favouriting is action-scoped).
 */
export function ExpertCard({
  expert,
  action,
  onToggleFavorite,
  isToggling = false,
}: {
  expert: DirectoryExpert;
  action: string;
  onToggleFavorite: (expert: DirectoryExpert) => void;
  isToggling?: boolean;
}) {
  const { t } = useTranslation();
  const issues = expert.covered_issues ?? [];
  const extraIssues = Math.max(0, issues.length - MAX_ISSUES);
  const originRegions = expert.origin_regions ?? [];
  const servedRegions = expert.service_regions ?? [];
  const MAX_REGIONS = 3;
  const hasContact =
    Boolean(expert.contact_primary_first_name) ||
    Boolean(expert.contact_primary_last_name);
  const monoTheme = thematicClasses(issues[0]?.css_class);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex gap-3">
        <div
          className={cn(
            "grid size-11 shrink-0 place-items-center rounded-xl text-base font-bold",
            monoTheme.softBg,
            monoTheme.ink,
          )}
          aria-hidden
        >
          {initials(expert)}
        </div>
        <div className="min-w-0 space-y-0.5">
          {/* The organisation is the entry's identity (as on the public
              profile); the répondant is who to talk to, so it reads as the
              secondary line. */}
          <p className="font-semibold leading-tight">
            {expert.organization_name}
          </p>
          {hasContact && (
            <p className="truncate text-sm text-muted-foreground">
              {`${expert.contact_primary_first_name ?? ""} ${expert.contact_primary_last_name ?? ""}`.trim()}
            </p>
          )}
          {expert.contact_primary_function && (
            <p className="text-xs text-muted-foreground">
              {expert.contact_primary_function}
            </p>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1.5 text-xs text-muted-foreground">
            {/* Building = where the expert is established (bureaux ou
                représentants), the only location the intake forms collect;
                radius = the regions it serves. */}
            <span
              className="inline-flex items-center gap-1.5"
              title={t("expert.profile.originRegions")}
            >
              <Building2 className="size-3.5 shrink-0" aria-hidden />
              <span className="font-medium">
                {t("expert.card.originLabel", { count: originRegions.length })}
              </span>
              {originRegions.length > 0 ? (
                <span>
                  {originRegions
                    .slice(0, MAX_REGIONS)
                    .map((r) => r.name)
                    .join(", ")}
                  {originRegions.length > MAX_REGIONS && (
                    <span className="italic">
                      {" "}
                      {t("expert.card.moreRegions", {
                        count: originRegions.length - MAX_REGIONS,
                      })}
                    </span>
                  )}
                </span>
              ) : (
                <span className="italic">{t("expert.card.regionUnknown")}</span>
              )}
            </span>
            {servedRegions.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5"
                title={t("expert.profile.servedRegions")}
              >
                <Radius className="size-3.5 shrink-0" aria-hidden />
                <span className="font-medium">
                  {t("expert.card.servedLabel", { count: servedRegions.length })}
                </span>
                <span>
                  {servedRegions
                    .slice(0, MAX_REGIONS)
                    .map((r) => r.name)
                    .join(", ")}
                  {servedRegions.length > MAX_REGIONS && (
                    <span className="italic">
                      {" "}
                      {t("expert.card.moreRegions", {
                        count: servedRegions.length - MAX_REGIONS,
                      })}
                    </span>
                  )}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {issues.slice(0, MAX_ISSUES).map((issue) => (
            <IssueChip key={issue.id} issue={issue} />
          ))}
          {extraIssues > 0 && (
            <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
              +{extraIssues}
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between border-t border-border/70 pt-3">
        <Link
          to="/experts/profile/$id"
          params={{ id: expert.id }}
          search={{ action }}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-theme-economic-ink underline-offset-4 hover:underline"
        >
          {t("expert.card.viewProfile")}
          <ArrowUpRight className="size-4" aria-hidden />
        </Link>
        <button
          type="button"
          aria-pressed={expert.is_favorite}
          aria-label={
            expert.is_favorite
              ? t("expert.card.unfavorite")
              : t("expert.card.favorite")
          }
          disabled={isToggling}
          onClick={() => onToggleFavorite(expert)}
          className={cn(
            "grid size-9 place-items-center rounded-lg border transition-colors",
            expert.is_favorite
              ? "border-destructive/30 bg-destructive/10"
              : "border-border bg-background hover:bg-accent",
            isToggling && "opacity-60",
          )}
        >
          <Heart
            className={cn(
              "size-[18px]",
              expert.is_favorite
                ? "fill-destructive text-destructive"
                : "text-muted-foreground",
            )}
            aria-hidden
          />
        </button>
      </div>
    </Card>
  );
}
